import { datadogRum } from '@datadog/browser-rum';
import { useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useFeatureFlags } from '|/app/services/launch-darkly/useFeatureFlags';
import { useBrand } from '|/brand';
import useIsInBucket from '|/hooks/use-is-in-bucket';

export const OPD_SEPARATION_EXPERIMENT = 'uw-verifx-opdds';

// insurance-quote still lives in Checkout.UI, not the OPD MFE. It reads referral/consent
// from the order-placed Redux slice, which only the in-monorepo (control) OPD populates on
// mount — the separately-bundled MFE has its own store and can't. Force control on this
// route until insurance-quote fetches that data itself.
const INSURANCE_QUOTE_ROUTE = 'order-placed/insurance-quote';

export const useOpdShellMode = () => {
  const { opdPurchaseUiSeparation: ldFlagEnabled } = useFeatureFlags();
  const { brand } = useBrand();
  // Slate purchases always use the OPD MFE, so they are excluded from the
  // experiment — enrolling them would pollute the bucket analytics (TXCC-9547).
  const isInTreatment = useIsInBucket(
    ldFlagEnabled && brand !== 'slate' ? OPD_SEPARATION_EXPERIMENT : '',
    'treatment',
    ['control']
  );
  const { pathname } = useLocation();
  const hasTracked = useRef(false);

  const isInsuranceQuoteRoute = pathname.includes(INSURANCE_QUOTE_ROUTE);
  // Slate purchases always use the OPD MFE, bypassing the experiment and the
  // insurance-quote control-forcing (TXCC-9547, Route 66).
  const shellModeAllowed =
    brand === 'slate' || (ldFlagEnabled && isInTreatment === true && !isInsuranceQuoteRoute);
  const isLoading = brand !== 'slate' && ldFlagEnabled && isInTreatment === null;

  // Resolved once the bucket lands, or immediately for Slate (never enrolled) so
  // Slate shell-mode usage stays observable regardless of experiment state.
  const isResolved = brand === 'slate' || (ldFlagEnabled && isInTreatment !== null);

  useEffect(() => {
    if (isResolved && !hasTracked.current) {
      hasTracked.current = true;
      const bucket =
        isInTreatment === true ? 'treatment' : isInTreatment === false ? 'control' : 'none';
      datadogRum.addAction('opd-shell-mode-resolved', {
        bucket,
        shellModeAllowed,
        ldFlagEnabled,
        // Attribute name preserved: RUM dashboards facet on it.
        isSlateVehicle: brand === 'slate'
      });
    }
  }, [isResolved, isInTreatment, shellModeAllowed, ldFlagEnabled, brand]);

  return { shellModeAllowed, isLoading };
};
